import { useState } from "react";
import { Trash2 } from "lucide-react";
import api from "../../../../../../utils/axiosInstance";
import UploadOutputFileModal from "./UploadOutputFileModal";
import { toast, confirmDialog } from "../../../../../../components/Toast/toast";

const OutputFilesCard = ({ jobId, files = [], onRefresh }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const formatFileSize = (bytes) => {
    if (!bytes) return "—";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDownloadSingle = async (file) => {
    try {
      const response = await api.get(
        `/download-file?path=${encodeURIComponent(file.file_path)}`,
        { responseType: "blob" }
      );

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_file_name || "file";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file");
    }
  };

  const handleDownloadZip = async () => {
    if (files.length === 0) {
      toast.error("No files to download");
      return;
    }

    try {
      setDownloading(true);
      const response = await api.get(
        `/job-output-files/download-zip?job_id=${jobId}`,
        { responseType: "blob" }
      );

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `job-${jobId}-output-files.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download zip error:", error);
      toast.error("Failed to download files as zip");
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async (file) => {
    if (!await confirmDialog(`Are you sure you want to delete "${file.original_file_name}"?`)) {
      return;
    }

    try {
      await api.delete(`/job-output-files/${file.id}`, { withCredentials: true });
      toast.success("File deleted successfully");
      onRefresh();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.message || "Failed to delete file");
    }
  };

  const HEADERS = [
    "File #",
    "Filename",
    "Size",
    "Uploaded at",
    "Uploaded by",
    "Input for jobs",
    "Is project output",
    "Actions",
  ];

  return (
    <>
      <section>
        <div className="flex justify-between items-center mb-3 flex-wrap gap-3">
          <h2 className="text-xl font-bold text-gray-900">
            Output files <span className="text-gray-500 font-medium">({files.length})</span>
          </h2>

          <div className="flex items-center gap-2 text-sm flex-wrap">
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
            >
              Upload
            </button>
            <button className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded">
              Add URL
            </button>
            <button
              onClick={handleDownloadZip}
              disabled={downloading || files.length === 0}
              className="flex items-center gap-2 px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded"
            >
              {downloading ? "Downloading..." : "Download as zip"}
            </button>
          </div>
        </div>

        <div className="w-full bg-white shadow rounded-lg overflow-hidden">
          <div className="max-h-[300px] overflow-y-auto overflow-x-auto">
            <table className="w-full table-auto border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                  {HEADERS.map((h) => (
                    <th key={h} className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {files.length === 0 ? (
                  <tr>
                    <td colSpan={HEADERS.length} className="py-10 text-center text-gray-400 text-xs">
                      No output files found.
                    </td>
                  </tr>
                ) : (
                  files.map((file, index) => {
                    const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                    return (
                      <tr key={file.id || index} className={`${rowBg} hover:bg-blue-50 transition-colors`}>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-700">
                          {file.file_code || `#${index + 1}`}
                        </td>
                        <td className="px-3 py-2 text-xs break-words">
                          <button
                            onClick={() => handleDownloadSingle(file)}
                            className="text-blue-600 hover:text-blue-800 underline font-medium"
                          >
                            {file.original_file_name || "—"}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-700">
                          {formatFileSize(file.file_size)}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                          {formatDateTime(file.uploaded_at)}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-700">
                          {file.adminUploader?.details
                            ? `${file.adminUploader.details.first_name} ${file.adminUploader.details.last_name}`
                            : file.vendorUploader
                            ? `${file.vendorUploader.company_name || "Vendor"}`
                            : "—"}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-700">
                          {file.input_for_job || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                          {file.is_project_output ? (
                            <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                              Yes
                            </span>
                          ) : (
                            <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                              No
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center whitespace-nowrap">
                          <Trash2
                            className="w-4 h-4 text-gray-400 hover:text-red-600 cursor-pointer transition-colors mx-auto"
                            onClick={() => handleDelete(file)}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadOutputFileModal
          jobId={jobId}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            onRefresh();
          }}
        />
      )}
    </>
  );
};

export default OutputFilesCard;